/**
 * Dates and times typed and shown as text, always DD/MM/YYYY and 24-hour HH:MM.
 *
 * The browser's own date and time fields follow the computer's regional settings
 * (a PC set to the United States showed 11/04/2026 for 4 November and would show
 * 8:00 AM). The team must see the same thing on every computer, so the app uses
 * text fields with these functions instead.
 */
const pad = (n) => String(n).padStart(2, '0');
function validDay(y, m, d) {
    if (y < 2000 || y > 2099 || m < 1 || m > 12 || d < 1)
        return false;
    const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
    return d <= last;
}
/** "2026-11-05" -> "05/11/2026"; anything else -> "". */
export function formatDateText(iso) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso ?? '');
    return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
}
/**
 * Text typed by a person -> "YYYY-MM-DD", or null when it is not a real date.
 * Day always comes first. Accepts 05/11/2026, 5/11/2026, 5-11-2026, 5.11.26, 05112026, 051126,
 * and 5/11 (year taken from `fallbackYear`).
 */
export function parseDateText(text, fallbackYear) {
    const s = text.trim();
    if (!s)
        return null;
    let d;
    let m;
    let y;
    const parts = s.split(/[/.\-\s]+/).filter(Boolean);
    if (parts.length >= 2 && parts.length <= 3 && parts.every((p) => /^\d+$/.test(p))) {
        d = Number(parts[0]);
        m = Number(parts[1]);
        if (parts.length === 3) {
            const yy = parts[2];
            if (yy.length === 2)
                y = 2000 + Number(yy);
            else if (yy.length === 4)
                y = Number(yy);
            else
                return null;
        }
        else {
            if (fallbackYear === undefined)
                return null;
            y = fallbackYear;
        }
        if (parts[0].length > 2 || parts[1].length > 2)
            return null;
    }
    else if (/^\d{8}$/.test(s)) {
        d = Number(s.slice(0, 2));
        m = Number(s.slice(2, 4));
        y = Number(s.slice(4));
    }
    else if (/^\d{6}$/.test(s)) {
        d = Number(s.slice(0, 2));
        m = Number(s.slice(2, 4));
        y = 2000 + Number(s.slice(4));
    }
    else {
        return null;
    }
    if (!validDay(y, m, d))
        return null;
    return `${y}-${pad(m)}-${pad(d)}`;
}
/** True when the text is a full DD/MM/YYYY date (used to apply it while the person types). */
export function isCompleteDateText(text) {
    return /^\d{2}\/\d{2}\/\d{4}$/.test(text.trim());
}
/**
 * Text typed by a person -> "HH:MM" (24 hours), or null when it is not a time.
 * Accepts 08:30, 8:30, 8h30, 8.30, 8h, 830, 0830, 8 and 14.
 */
export function parseTimeText(text) {
    const s = text.trim().toLowerCase();
    if (!s)
        return null;
    let h;
    let min;
    let m = /^(\d{1,2})\s*[:h.]\s*(\d{2})?$/.exec(s);
    if (m) {
        h = Number(m[1]);
        min = m[2] ? Number(m[2]) : 0;
    }
    else if ((m = /^(\d{1,2})$/.exec(s))) {
        h = Number(m[1]);
        min = 0;
    }
    else if ((m = /^(\d{1,2})(\d{2})$/.exec(s))) {
        h = Number(m[1]);
        min = Number(m[2]);
    }
    else {
        return null;
    }
    if (h > 23 || min > 59)
        return null;
    return `${pad(h)}:${pad(min)}`;
}
/** "08:30:00" or "08:30" -> "08:30"; anything else -> "". */
export function formatTimeText(value) {
    const m = /^(\d{2}):(\d{2})/.exec(value ?? '');
    return m ? `${m[1]}:${m[2]}` : '';
}
//# sourceMappingURL=dateText.js.map